/**
 * Simple hash-based SPA router with role-based route guards.
 * Supports parameterised routes (e.g. /aircraft/:id).
 * No framework dependency — vanilla JS.
 */

import { getUser, getRole } from './auth.js';
import { updateSidebarActiveLink } from '../components/sidebar.js';

const routes = {};
let lastRenderedHash = null;

/**
 * Role hierarchy: owner > admin > pilot.
 * A higher role can access routes allowed for lower roles.
 */
const ROLE_HIERARCHY = { owner: 3, admin: 2, pilot: 1 };

/**
 * Check whether a user role satisfies a list of allowed roles.
 * Returns true if the user's role is at or above any of the allowed roles.
 */
export function roleCanAccess(userRole, allowedRoles) {
  if (!allowedRoles || allowedRoles.length === 0) return true;
  const userLevel = ROLE_HIERARCHY[userRole] || 0;
  return allowedRoles.some((r) => userLevel >= (ROLE_HIERARCHY[r] || 0));
}

let initialized = false;

export function initRouter() {
  if (initialized) return;
  initialized = true;
  window.addEventListener('hashchange', handleRoute);
  handleRoute();
}

/**
 * registerRoute(path, handler, options?)
 * @param {string} path - Hash route path (e.g. '/', '/settings', '/aircraft/:id')
 * @param {Function} handler - Page render function. Receives { params } for parameterised routes.
 * @param {Object} [options]
 * @param {string[]} [options.roles] - Allowed roles. If omitted, all authenticated users can access.
 */
export function registerRoute(path, handler, options) {
  routes[path] = { handler, roles: options?.roles || null };
}

export function navigate(path) {
  if (window.location.hash === '#' + path) return;
  lastRenderedHash = null;
  window.location.hash = path;
}

export function resetLastRenderedHash() {
  lastRenderedHash = null;
}

/**
 * Match a hash path against a registered route pattern.
 * Returns { route, params } if matched, or null.
 */
function matchRoute(hash) {
  for (const pattern of Object.keys(routes)) {
    // Skip patterns without params — those are handled by exact match
    if (!pattern.includes(':')) continue;

    const patternParts = pattern.split('/');
    const hashParts = hash.split('/');

    if (patternParts.length !== hashParts.length) continue;

    const params = {};
    let matched = true;

    for (let i = 0; i < patternParts.length; i++) {
      if (patternParts[i].startsWith(':')) {
        // Named parameter — extract value
        params[patternParts[i].slice(1)] = hashParts[i];
      } else if (patternParts[i] !== hashParts[i]) {
        matched = false;
        break;
      }
    }

    if (matched) {
      return { route: routes[pattern], params };
    }
  }

  return null;
}

function handleRoute() {
  const fullHash = window.location.hash.slice(1) || '/';

  // Skip re-render if we're already on this route
  if (fullHash === lastRenderedHash) return;

  // Separate path from query string (e.g. /log-flight?id=abc)
  const qIndex = fullHash.indexOf('?');
  const hash = qIndex >= 0 ? fullHash.slice(0, qIndex) : fullHash;
  const query = {};
  if (qIndex >= 0) {
    const searchParams = new URLSearchParams(fullHash.slice(qIndex + 1));
    for (const [k, v] of searchParams) query[k] = v;
  }

  // 1. Try exact match first (existing behaviour)
  let route = routes[hash];
  let params = {};

  // 2. If no exact match, try pattern matching for parameterised routes
  if (!route) {
    const match = matchRoute(hash);
    if (match) {
      route = match.route;
      params = match.params;
    }
  }

  if (!route) {
    // Default: show 404 or redirect to dashboard
    const fallback = routes['/'] || routes['/login'];
    if (fallback) fallback.handler();
    return;
  }

  // Require authentication for all routes except /login
  const user = getUser();
  if (!user && hash !== '/login') {
    navigate('/login');
    return;
  }

  // If route has role restrictions, enforce them
  if (route.roles) {
    if (!user) {
      navigate('/login');
      return;
    }

    const role = getRole();
    if (!roleCanAccess(role, route.roles)) {
      sessionStorage.setItem('flash', 'Access denied — you do not have permission to view this page');
      navigate('/');
      return;
    }
  }

  // Dismiss any sticky overlays (e.g. profile modal) when navigating
  document.getElementById('profile-modal-overlay')?.remove();

  lastRenderedHash = fullHash;
  route.handler({ params, query });
  updateSidebarActiveLink(hash);
}
